import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd"
import { useTareas } from "../context/TareasContext"
import { PRIORITY_CONFIG } from "../context/TareasContext"
import AssigneeAvatar from "../components/AssigneeAvatar"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"
import { FaPlus, FaClock, FaLink, FaUserPlus, FaTimes } from "react-icons/fa"
import { MdDragIndicator } from "react-icons/md"
import { useState, useRef, useEffect } from "react"

const PRIORITIES = ["Baja", "Media", "Alta", "Urgente"]
const PRIORITY_ICONS = { Baja: "↓", Media: "→", Alta: "↑", Urgente: "⚡" }

const hexToRgba = (hex, alpha = 0.15) => {
  if (!hex || !hex.startsWith("#")) return `rgba(107,114,128,${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

const formatDateShort = (date) => {
  if (!date) return null
  const d = new Date(date)
  const months = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"]
  return `${String(d.getDate()).padStart(2,"0")} ${months[d.getMonth()]}`
}


const deltaToPlainText = (content) => {
  if (!content) return ""
  if (typeof content === "string") return content
  if (typeof content === "object" && content?.ops) {
    return content.ops.map(op => (typeof op.insert === "string" ? op.insert : "")).join("").trim()
  }
  return ""
}

const TaskCard = ({ task, index, openTask }) => {
  const { updateTask, workers, selectedProject } = useTareas()
  const isPast = task.limitDate && new Date(task.limitDate) < new Date()
  const assignees = task.assignees || []
  const assigneeIds = assignees.map(a => (typeof a === "object" ? a.id : a)).filter(Boolean)
  const linksCount = (task.adminLinks?.length || 0) + (task.workerLinks?.length || 0)
  const subtasks = task.subtasks || []
  const completedSubs = subtasks.filter(s => s.completed).length
  const coverColor = task.coverColor || null

  // título inline
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState(task.task)
  const inputRef = useRef(null)

  // prioridad popover
  const [showPriority, setShowPriority] = useState(false)
  const priorityRef = useRef(null)

  // asignados popover
  const [showAssignees, setShowAssignees] = useState(false)
  const assigneesRef = useRef(null)

  // cierra popovers al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (priorityRef.current && !priorityRef.current.contains(e.target)) setShowPriority(false)
      if (assigneesRef.current && !assigneesRef.current.contains(e.target)) setShowAssignees(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const startEdit = (e) => {
    e.stopPropagation()
    setTitleValue(task.task)
    setEditingTitle(true)
    setTimeout(() => inputRef.current?.select(), 0)
  }

  const commitEdit = async () => {
    setEditingTitle(false)
    const trimmed = titleValue.trim()
    if (trimmed && trimmed !== task.task) {
      await updateTask(task.id, { task: trimmed })
    } else {
      setTitleValue(task.task)
    }
  }

  const handlePriorityChange = async (p) => {
    setShowPriority(false)
    await updateTask(task.id, { priority: p })
  }

  const handleAddAssignee = async (workerId) => {
    if (!workerId || assigneeIds.includes(workerId)) return
    const next = [...assigneeIds, workerId]
    await updateTask(task.id, { assignees: next })
  }

  const handleRemoveAssignee = async (workerId) => {
    const next = assigneeIds.filter(id => id !== workerId)
    await updateTask(task.id, { assignees: next })
  }

  const availableWorkers = workers.filter(w => !assigneeIds.includes(w.id))
  const priorityConfig = task.priority ? (PRIORITY_CONFIG[task.priority] ?? { color: "#6B7280", bg: "#F3F4F6" }) : null

  const projectTagDefs = selectedProject?.tags || []
  const projectTagIdsOnTask = (task.projectTags || [])
    .map((x) => (typeof x === "object" && x != null ? x.tagId : x))
    .filter(Boolean)

  const isInteracting = editingTitle || showPriority || showAssignees

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`bg-white rounded-xl border border-gray-100 p-3 mb-2 cursor-pointer transition-all duration-150 overflow-visible ${
            snapshot.isDragging
              ? "shadow-lg rotate-1 scale-105 border-orange-200"
              : "hover:shadow-md hover:border-gray-200"
          }`}
          style={{
            ...provided.draggableProps.style,
            ...(coverColor ? { borderLeftWidth: 4, borderLeftColor: coverColor } : {}),
          }}
          onClick={() => { if (!isInteracting) openTask(task) }}
        >
          {/* Drag handle + Prioridad */}
          <div
            {...provided.dragHandleProps}
            className="flex items-center justify-between mb-2"
            onClick={e => e.stopPropagation()}
          >
            <MdDragIndicator className="text-gray-300 text-base cursor-grab active:cursor-grabbing" />

            {/* Prioridad clickeable */}
            <div ref={priorityRef} className="relative">
              <button
                onClick={e => { e.stopPropagation(); setShowPriority(v => !v) }}
                className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium transition-colors hover:opacity-80"
                style={priorityConfig
                  ? { backgroundColor: priorityConfig.bg, color: priorityConfig.color }
                  : { backgroundColor: "#F3F4F6", color: "#9CA3AF" }
                }
              >
                {task.priority ? (
                  <><span>{PRIORITY_ICONS[task.priority]}</span>{task.priority}</>
                ) : (
                  <span className="text-gray-400">Prioridad</span>
                )}
              </button>
              {showPriority && (
                <div
                  className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 flex flex-col gap-0.5 min-w-[110px]"
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    onClick={() => handlePriorityChange("")}
                    className="text-left text-[10px] px-2 py-1 rounded-lg hover:bg-gray-50 text-gray-400"
                  >
                    Sin prioridad
                  </button>
                  {PRIORITIES.map(p => {
                    const cfg = PRIORITY_CONFIG[p]
                    return (
                      <button
                        key={p}
                        onClick={() => handlePriorityChange(p)}
                        className="text-left text-[10px] px-2 py-1 rounded-lg flex items-center gap-1.5 font-medium"
                        style={{ color: cfg.color, backgroundColor: task.priority === p ? cfg.bg : "transparent" }}
                      >
                        <span>{PRIORITY_ICONS[p]}</span>{p}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Título inline */}
          {editingTitle ? (
            <textarea
              ref={inputRef}
              value={titleValue}
              onChange={e => setTitleValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitEdit() }
                if (e.key === "Escape") { setEditingTitle(false); setTitleValue(task.task) }
              }}
              onClick={e => e.stopPropagation()}
              rows={2}
              className="w-full text-xs font-semibold text-gray-800 leading-tight mb-1 resize-none border border-orange-300 rounded-lg px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
              autoFocus
            />
          ) : (
            <p
              className="text-xs font-semibold text-gray-800 leading-tight mb-1 whitespace-normal break-words cursor-text"
              onClick={startEdit}
            >
              {task.task}
            </p>
          )}

          {/* Tipo + Etiquetas + Tags proyecto */}
          {(task.tipo?.name || (task.etiquetas?.length > 0) || projectTagIdsOnTask.length > 0) && (
            <div className="flex flex-wrap gap-1 mb-2">
              {task.tipo?.name && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{task.tipo.name}</span>
              )}
              {task.etiquetas?.slice(0, 2).map(e => (
                <span key={e?.id || e?.name} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                  {e?.name || e}
                </span>
              ))}
              {task.etiquetas?.length > 2 && (
                <span className="text-[9px] text-gray-400">+{task.etiquetas.length - 2}</span>
              )}
              {projectTagIdsOnTask.slice(0, 4).map((tid) => {
                const def = projectTagDefs.find((t) => t.id === tid)
                const label = def?.name || tid.slice(0, 6)
                const col = def?.color || "#6B7280"
                return (
                  <span
                    key={tid}
                    className="text-[9px] px-1.5 py-0.5 rounded border border-gray-100 max-w-[88px] truncate"
                    style={{ color: col, backgroundColor: `${col}20` }}
                    title={label}
                  >
                    {label}
                  </span>
                )
              })}
              {projectTagIdsOnTask.length > 4 && (
                <span className="text-[9px] text-gray-400">+{projectTagIdsOnTask.length - 4}</span>
              )}
            </div>
          )}

          {/* Notas */}
          {deltaToPlainText(task.adminNotes) && (
            <p className="text-[10px] text-gray-400 line-clamp-1 mb-2">{deltaToPlainText(task.adminNotes)}</p>
          )}

          {/* Footer: asignados + meta */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">

              {/* Asignados */}
              <div ref={assigneesRef} className="relative flex items-center gap-1" onClick={e => e.stopPropagation()}>
                {assignees.map(a => {
                  const id = typeof a === "object" ? a.id : a
                  const name = typeof a === "object" ? a.name : workers.find(w => w.id === id)?.name || id
                  return (
                    <div key={id} className="group/av relative">
                      <AssigneeAvatar name={name} size={20} user={typeof a === "object" ? a : workers.find(w => w.id === id)} />
                      <button
                        onClick={e => { e.stopPropagation(); handleRemoveAssignee(id) }}
                        className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 text-white rounded-full items-center justify-center text-[7px] hidden group-hover/av:flex"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  )
                })}

                {availableWorkers.length > 0 && (
                  <>
                    <button
                      onClick={e => { e.stopPropagation(); setShowAssignees(v => !v) }}
                      className="w-5 h-5 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-orange-400 hover:text-orange-400 transition-colors"
                    >
                      <FaUserPlus className="text-[8px]" />
                    </button>
                    {showAssignees && (
                      <div
                        className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 flex flex-col gap-0.5 min-w-[150px]"
                        onClick={e => e.stopPropagation()}
                      >
                        {availableWorkers.map(w => (
                          <button
                            key={w.id}
                            onClick={() => { handleAddAssignee(w.id); setShowAssignees(false) }}
                            className="flex items-center gap-2 text-[10px] px-2 py-1.5 rounded-lg hover:bg-orange-50 text-gray-700 text-left"
                          >
                            <AssigneeAvatar name={w.name} size={16} user={w} />
                            {formatWorkerDisplayName(w.name || w.email)}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {linksCount > 0 && (
                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                  <FaLink className="text-[9px]" />{linksCount}
                </span>
              )}
              {subtasks.length > 0 && (
                <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                  ✓ {completedSubs}/{subtasks.length}
                </span>
              )}
            </div>

            {task.limitDate && (
              <span className={`flex items-center gap-1 text-[10px] flex-shrink-0 ${isPast ? "text-red-500" : "text-gray-400"}`}>
                <FaClock className="text-[9px]" />
                {formatDateShort(task.limitDate)}
              </span>
            )}
          </div>
        </div>
      )}
    </Draggable>
  )
}

const KanbanView = () => {
  const { tasks, setTasks, updateTask, setCreateModalOpen, openTask, getTaskStatusId, estadosTareas, filters } = useTareas()

  const selectedCats = filters.categories || []
  const selectedStatuses = filters.status || []
  const statusColumns = estadosTareas
    .filter(e => {
      if (selectedStatuses.length > 0) return selectedStatuses.includes(e.id)
      if (selectedCats.length > 0) return selectedCats.includes(e.category || "No iniciado")
      return true
    })
    .map(e => ({
      id: e.id,
      label: e.name,
      color: e.color || "#6B7280",
      bg: hexToRgba(e.color || "#6B7280", 0.08),
    }))

  const UNASSIGNED_ID = "__sin_estado__"
  const columns = [
    ...statusColumns,
    { id: UNASSIGNED_ID, label: "Sin estado", color: "#9CA3AF", bg: hexToRgba("#9CA3AF", 0.08) },
  ]

  const getColumnTasks = (estadoId) => {
    if (estadoId === UNASSIGNED_ID) {
      return tasks.filter(t => {
        const taskStatusId = getTaskStatusId(t)
        if (taskStatusId == null) return true
        const matchesAny = statusColumns.some(col => String(col.id) === String(taskStatusId))
        return !matchesAny
      })
    }
    return tasks.filter(t => {
      const taskStatusId = getTaskStatusId(t)
      if (taskStatusId == null) return false
      return String(taskStatusId) === String(estadoId)
    })
  }

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result
    if (!destination) return
    if (source.droppableId === destination.droppableId && source.index === destination.index) return

    const destId = destination.droppableId
    const taskId = draggableId
    const prevStatusId = source.droppableId
    const statusId = destId === UNASSIGNED_ID ? null : destId

    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: statusId } : t))

    try {
      await updateTask(taskId, { status: statusId })
    } catch {
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: prevStatusId === UNASSIGNED_ID ? null : prevStatusId } : t))
    }
  }

  if (statusColumns.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400">
        <p className="text-sm">No hay estados configurados para este proyecto. Configura estados globales o del proyecto en el admin.</p>
      </div>
    )
  }

  return (
    <div className="h-full overflow-x-auto px-4 py-4">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 h-full min-w-max">
          {columns.map(col => {
            const colTasks = getColumnTasks(col.id)
            return (
              <div
                key={col.id}
                className="flex flex-col w-72 flex-shrink-0"
              >
                {/* Column header */}
                <div
                  className="flex items-center justify-between px-3 py-2.5 rounded-t-xl border border-b-0"
                  style={{ backgroundColor: col.bg, borderColor: `${col.color}30` }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: col.color }}
                    />
                    <span className="text-xs font-semibold text-gray-700">{col.label}</span>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: hexToRgba(col.color, 0.2), color: col.color }}
                    >
                      {colTasks.length}
                    </span>
                  </div>
                  {col.id !== UNASSIGNED_ID && (
                    <button
                      onClick={() => setCreateModalOpen(true)}
                      className="p-1 rounded-lg hover:bg-white/70 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Nueva tarea"
                    >
                      <FaPlus className="text-[10px]" />
                    </button>
                  )}
                </div>

                {/* Droppable column */}
                <Droppable droppableId={col.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 p-2 rounded-b-xl border min-h-[200px] max-h-[calc(100vh-220px)] overflow-y-auto transition-colors ${
                        snapshot.isDraggingOver
                          ? "bg-orange-50/60 border-orange-200"
                          : "border-gray-100 bg-gray-50/50"
                      }`}
                      style={{ borderColor: `${col.color}30` }}
                    >
                      {colTasks.map((task, i) => (
                        <TaskCard key={task.id} task={task} index={i} openTask={openTask} />
                      ))}
                      {provided.placeholder}

                      {colTasks.length === 0 && !snapshot.isDraggingOver && (
                        <div className="flex items-center justify-center h-20 text-[10px] text-gray-300">
                          Arrastra aquí
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            )
          })}
        </div>
      </DragDropContext>
    </div>
  )
}

export default KanbanView
